import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  Clock, 
  Star, 
  ArrowRight,
  MapPin,
  Award,
  Film,
  Coffee,
  ShoppingBag,
  Home,
  Thermometer,
  CheckCircle
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const GroupToursPage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Group Tours - Umoona Opal Mine",
    "description": "Educational group tours and school excursions at Umoona Opal Mine, Coober Pedy. Customized underground mining experiences for schools and large groups.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "openingHours": "Mo-Su 08:30-17:30",
    "url": "https://umoonaopalmine.com.au/group-tours"
  };

  const tourSteps = [
    {
      step: "01",
      title: "Coober Pedy Town Tour",
      description: "One of our hostesses will meet your group upon arrival at Umoona and take them on a tour of Coober Pedy – including the underground churches and other sites of interest in the town.",
      image: "/group-tour-step-01.jpg",
      note: "Included in tour"
    },
    {
      step: "02", 
      title: "Opal Documentary & Demonstration",
      description: "We show your group our award winning documentary 'The Story of Opal', and present a hands-on cutting and polishing demonstration – explaining the different types of Opal.",
      image: "/assets/2022-1018518.jpg",
      note: "Included in tour"
    },
    {
      step: "03",
      title: "Underground Homes & Mine", 
      description: "Following the presentation – we'll take your group into our underground homes and then deep into the original opal mine.",
      image: "/assets/Umoona2025-P1043661.jpg",
      note: "Included in tour"
    },
    {
      step: "04",
      title: "Shopping & Refreshments",
      description: "Your group can look through our displays, shop for opals, jewellery or souvenirs in a relaxed atmosphere whilst enjoying complimentary tea or coffee.",
      image: "/group-tour-step-04.jpg", 
      note: "Included in tour"
    }
  ];

  const features = [
    {
      icon: Clock,
      title: "Extended Tours",
      description: "Comprehensive 1.5-hour experience covering all major attractions"
    },
    {
      icon: Users,
      title: "Custom Groups", 
      description: "Tailored experiences for both small and large group sizes"
    },
    {
      icon: Award,
      title: "Award-Winning Film",
      description: "'The Story of Opal' documentary plus live demonstrations"
    },
    {
      icon: Coffee,
      title: "Refreshments Included",
      description: "Complimentary tea or coffee in relaxed shopping atmosphere"
    }
  ];

  return (
    <div>
      <SEOHead
        title="School & Group Tours - Educational Mining Experience | Umoona Opal Mine"
        description="Educational group tours and school excursions at Umoona Opal Mine, Coober Pedy. Customized underground mining experiences for schools and large groups."
        keywords="group tours, school tours, educational tours, Coober Pedy group bookings, mining excursions"
        canonical="/group-tours"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/assets/Umoona2025-P1043661.jpg"
            alt="Group Tours at Umoona Opal Mine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl">
            <Badge variant="secondary" className="mb-6 bg-orange-500/20 text-orange-300 border border-orange-400/30 px-6 py-3 text-lg">
              👥 Extended & Custom Tours • Small & Big Groups
            </Badge>
            
            <h1 className="text-4xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Group Tours
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
                at Umoona
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              The Umoona Opal Mine & Museum offers extended and Custom Tours to suit small & big Groups.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
                <Users className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                <div className="text-2xl font-bold text-white mb-2">Schools and Groups</div>
                <div className="text-orange-200">An educational experience</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
                <Star className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                <div className="text-2xl font-bold text-white mb-2">CUSTOM</div>
                <div className="text-yellow-200">Tours Available</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="mining-cta font-bold px-8 py-4 text-lg">
                <Link to="/contact">
                  Book Group Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 text-lg backdrop-blur-sm"
              >
                <Link to="/group-accommodation">
                  Group Accommodation
                  <Home className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How Group Tours Work */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How Our Group Tours Work
            </h2>
            <p className="text-xl text-gray-600">
              The complete tour takes approximately 1.5 hours and includes multiple unique experiences
            </p>
          </div>

          <div className="space-y-16">
            {tourSteps.map((step, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-cols-2 lg:flex-row-reverse' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center space-x-4">
                    <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                      {step.step}
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      {step.note}
                    </Badge>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img 
                    src={step.image}
                    alt={`Step ${step.step}: ${step.title}`}
                    className="w-full rounded-xl shadow-lg"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Group Tours */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Group Tours?
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive experiences designed specifically for groups
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                <CardContent className="space-y-4 p-0">
                  <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <feature.icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Underground Accommodation Teaser */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <Badge className="mb-4 bg-blue-100 text-blue-800 px-6 py-2 text-lg">
                🏠 Complete Group Experience
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Stay Underground
                <span className="block text-blue-600">Group Accommodation</span>
              </h2>
              <p className="text-xl text-gray-600">
                Complete your group experience with our unique underground accommodation. 
                Stay where the temperature is a constant 24°C year-round.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">102</div>
                <div className="text-gray-600">Maximum Beds</div>
                <div className="text-sm text-gray-500">Underground Bunkhouses</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">24°C</div>
                <div className="text-gray-600">Constant Temperature</div>
                <div className="text-sm text-gray-500">Year-Round Comfort</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">4-6</div>
                <div className="text-gray-600">People Per Room</div>
                <div className="text-sm text-gray-500">Comfortable Arrangement</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <img 
                src="/assets/DEL_4290.jpg"
                alt="Underground Bunkhouses"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img 
                src="/assets/DEL_4292.jpg"
                alt="Bunkhouse Interior"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img 
                src="/assets/DEL_4282.jpg"
                alt="Underground Rooms"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>

            <div className="text-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Button asChild size="lg" className="mining-cta">
                  <Link to="/group-accommodation">
                    View Accommodation Details
                    <Home className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/contact">
                    Contact for Booking
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Book Your Group Tour?
              </h2>
              <p className="text-xl text-orange-100 max-w-2xl mx-auto">
                Contact us to arrange your extended group tour experience at Australia's most iconic 
                underground opal mine and museum.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-orange-600 hover:bg-orange-50 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/contact">
                  Book Group Tour Now
                  <Users className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/group-accommodation">
                  Group Accommodation
                  <Home className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GroupToursPage;